import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCart } from '@/context/CartContext';
import { useFavorites } from '@/context/FavoritesContext';
import { useSite } from '@/context/SiteContext';

export const ProductCard = ({ product, showQuickView = true }) => {
  const { addToCart, formatPrice } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();
  const { formatPrice: siteFormatPrice } = useSite();

  const formatPriceDisplay = formatPrice || siteFormatPrice;
  const hasDiscount = product.oldPrice && product.oldPrice > product.price;
  const discountPercent = hasDiscount 
    ? Math.round((1 - product.price / product.oldPrice) * 100) 
    : 0;

  const favorite = isFavorite(product.id);

  return (
    <div className="group card-festa flex flex-col h-full bg-card">
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden rounded-t-2xl bg-muted/30">
        <Link to={`/produto/${product.id}`}>
          <img
            src={product.images?.[0] || 'https://via.placeholder.com/400x400?text=Produto'}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
          />
        </Link>
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {product.isNew && (
            <Badge className="badge-festa-secondary">
              Novidade
            </Badge>
          )}
          {hasDiscount && (
            <Badge className="badge-discount">
              -{discountPercent}%
            </Badge>
          )}
          {!product.inStock && (
            <Badge variant="secondary" className="bg-muted text-muted-foreground">
              Esgotado
            </Badge>
          )}
        </div>

        {/* Quick Actions */}
        <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Button
            variant="secondary"
            size="icon"
            className={`h-9 w-9 rounded-full shadow-md ${
              favorite ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground hover:bg-primary hover:text-primary-foreground'
            }`}
            onClick={(e) => {
              e.preventDefault();
              toggleFavorite(product);
            }}
          >
            <Heart className={`h-4 w-4 ${favorite ? 'fill-current' : ''}`} />
          </Button>
          {showQuickView && (
            <Link to={`/produto/${product.id}`}>
              <Button
                variant="secondary"
                size="icon"
                className="h-9 w-9 rounded-full shadow-md bg-card text-foreground hover:bg-secondary hover:text-secondary-foreground"
              >
                <Eye className="h-4 w-4" />
              </Button>
            </Link>
          )}
        </div>

        {/* Add to Cart Overlay */}
        {product.inStock && (
          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-foreground/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <Button
              className="w-full btn-festa text-sm py-2"
              onClick={(e) => {
                e.preventDefault();
                addToCart(product);
              }}
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              Adicionar
            </Button>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="flex flex-col flex-1 p-4">
        <Link to={`/produto/${product.id}`} className="flex-1">
          <h3 className="font-semibold text-foreground text-sm sm:text-base line-clamp-2 mb-2 group-hover:text-primary transition-colors">
            {product.name}
          </h3>
        </Link>
        
        <div className="flex items-center gap-2 mt-auto">
          <span className="text-lg font-bold text-primary">
            {formatPriceDisplay(product.price)}
          </span>
          {hasDiscount && (
            <span className="text-sm text-muted-foreground line-through">
              {formatPriceDisplay(product.oldPrice)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
